import React, { useEffect, useState } from "react";

import { SafeMarkdown } from "./SafeMarkdown";
import type { GroupTalkReportResponse } from "../lib/api";
import {
  GROUP_TALK_REPORT_FAILED_MESSAGE,
  GROUP_TALK_REPORT_NOT_READY_MESSAGE,
  canRegenerateGroupTalkReport,
  formatGroupTalkReportGeneratedAt,
  getGroupTalkCompletedReportTitle,
  isGroupTalkReportStale,
} from "../lib/groupTalkReport";

type GroupTalkReportModalProps = {
  isOpen: boolean;
  report: GroupTalkReportResponse | null;
  loading: boolean;
  creating: boolean;
  errorMessage: string;
  canCreate: boolean;
  onCreate: () => void;
  onClose: () => void;
};

export function GroupTalkReportModal({
  isOpen,
  report,
  loading,
  creating,
  errorMessage,
  canCreate,
  onCreate,
  onClose,
}: GroupTalkReportModalProps) {
  const isCompletedStaleReport = report?.status === "completed" && report.is_stale === true;
  const [showPreviousVersion, setShowPreviousVersion] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && isCompletedStaleReport) {
      setShowPreviousVersion(false);
    }
  }, [isCompletedStaleReport, isOpen, report?.generated_at, report?.source_hash]);

  if (!isOpen) return null;

  const completedReport = report?.status === "completed" ? report : null;
  const staleReport = isGroupTalkReportStale(report);
  const canRegenerate = canRegenerateGroupTalkReport(report);
  const reportTitle = completedReport ? getGroupTalkCompletedReportTitle(completedReport) : "論点整理レポート";
  const reportGeneratedAt = completedReport ? formatGroupTalkReportGeneratedAt(completedReport.generated_at) : "";
  const showGenerating = creating || report?.status === "generating";
  const canShowCreateAction =
    canCreate &&
    !errorMessage &&
    (report?.status === "not_generated" ||
      (report?.status === "failed" && report.retryable !== false) ||
      (staleReport && canRegenerate));
  const showCreate =
    !loading && !creating && canShowCreateAction;
  const showNotReady = !loading && !creating && (!report || report.status === "not_generated") && !canCreate;
  const createButtonLabel = staleReport ? "最新の内容で更新" : report?.status === "failed" ? "もう一度作成する" : "論点整理を作成";
  const generatingMessage = staleReport ? "論点整理を更新しています..." : "論点整理を作成しています...";
  const generatingDescription = staleReport
    ? "最新の議論を反映した内容にまとめ直しています。少しお待ちください。"
    : "会話で出た論点・選択肢・確認事項・次の一手を整理しています。";

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#2B211D]/45 px-4 py-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="group-talk-report-modal-title"
        className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-[1.6rem] border border-[#EADBC8] bg-[#FFFDFB] shadow-[0_28px_80px_rgba(48,35,30,0.26)]"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-[#F0E7DF] px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs font-black tracking-[0.16em] text-[#A67C52]">論点整理</p>
            <h2 id="group-talk-report-modal-title" className="mt-1 break-words text-xl font-black text-[#5D4D46] [overflow-wrap:anywhere]">
              {reportTitle}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="min-h-[40px] shrink-0 rounded-full border border-[#EADBC8] bg-white px-3 py-1.5 text-sm font-black text-[#7C5A4E] transition hover:border-[#CFAE9B] hover:bg-[#FFF7F1]"
          >
            ×
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          <p className="text-sm leading-7 text-[#6B5851]">
            会話で出た論点・選択肢・確認事項・次の一手を、あとで見返せる形に整理します。
          </p>

          {reportGeneratedAt ? (
            <p className="mt-3 inline-flex rounded-full bg-[#F5EDE8] px-3 py-1 text-xs font-bold text-[#7C5A4E]">
              生成日時: {reportGeneratedAt}
            </p>
          ) : null}

          {loading ? (
            <p className="mt-4 rounded-[1rem] border border-dashed border-[#D9C5B9] bg-white px-4 py-3 text-sm font-bold text-[#6B5851]">
              論点整理の状態を確認しています...
            </p>
          ) : null}

          {showGenerating ? (
            <div className="mt-5 flex min-h-[180px] flex-col items-center justify-center rounded-[1.2rem] border border-dashed border-[#D9C5B9] bg-white px-5 py-8 text-center">
              <p className="text-base font-black text-[#5D4D46]">{generatingMessage}</p>
              <p className="mt-3 max-w-xl text-sm font-bold leading-7 text-[#6B5851]">{generatingDescription}</p>
            </div>
          ) : null}

          {errorMessage ? (
            <p role="alert" className="mt-4 rounded-[1rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm font-bold text-[#A1493E]">
              {errorMessage}
            </p>
          ) : null}

          {!showGenerating && showNotReady ? (
            <p className="mt-4 rounded-[1rem] border border-dashed border-[#D9C5B9] bg-white px-4 py-3 text-sm font-bold text-[#6B5851]">
              {GROUP_TALK_REPORT_NOT_READY_MESSAGE}
            </p>
          ) : null}

          {!showGenerating && staleReport ? (
            <div className="mt-4 rounded-[1rem] border border-[#E4C56C] bg-[#FFF9E8] px-4 py-4 text-sm font-bold leading-6 text-[#7C5A2F]">
              <p>この論点整理は、現在の会話内容より前に作成されたものです。</p>
              <p>最新の議論を反映するには更新してください。</p>
              {showCreate ? (
                <button
                  type="button"
                  onClick={onCreate}
                  disabled={creating}
                  className="mt-4 min-h-[44px] rounded-[1.1rem] bg-[#5D4D46] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {createButtonLabel}
                </button>
              ) : null}
            </div>
          ) : null}

          {!showGenerating && staleReport && completedReport?.markdown_body ? (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowPreviousVersion((current) => !current)}
                className="rounded-full border border-[#EADBC8] bg-white px-3 py-1.5 text-xs font-black text-[#7C5A4E] transition hover:bg-[#FFF7F1]"
              >
                {showPreviousVersion ? "前回作成版を隠す" : "前回作成版を表示"}
              </button>
            </div>
          ) : null}

          {!showGenerating && completedReport?.markdown_body && (!staleReport || showPreviousVersion) ? (
            <div className="mt-5 rounded-[1.2rem] border border-[#F2E6DA] bg-white px-4 py-4">
              {staleReport ? (
                <p className="mb-3 text-xs font-black tracking-[0.12em] text-[#A67C52]">前回作成版</p>
              ) : null}
              <SafeMarkdown text={completedReport.markdown_body} />
            </div>
          ) : null}

          {!showGenerating && report?.status === "failed" ? (
            <div className="mt-4 rounded-[1rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm font-bold text-[#A1493E]">
              <p>{GROUP_TALK_REPORT_FAILED_MESSAGE}</p>
            </div>
          ) : null}
        </div>

        {showCreate && !staleReport ? (
          <footer className="shrink-0 border-t border-[#F0E7DF] px-5 py-4">
            <button
              type="button"
              onClick={onCreate}
              disabled={creating}
              className="min-h-[44px] rounded-[1.1rem] bg-[#5D4D46] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {creating ? generatingMessage : createButtonLabel}
            </button>
          </footer>
        ) : null}
      </section>
    </div>
  );
}
